import { useEffect, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useDebounce } from '@odo/shared-ui';
import { useUpdateUser, useUsersList } from '../../../../hooks/useUsers';
import { useToast } from '../../../../providers/ToastProvider';
import type { UserListItem } from '../../../../services/users';
import { apiErrorMessage } from '../../../../utils/apiError';
import { RoleBadge } from '../../users/RoleBadge';
import { UserCell } from '../../users/UserCell';

const SEARCH_DEBOUNCE_MS = 250;
const CANDIDATE_LIMIT = 8;

interface ExistingUserPaneProps {
    practiceId: string;
    practiceName: string;
    /** Emails already linked to this practice - filtered out of the candidate list. */
    existingEmails: Set<string>;
    onClose: () => void;
    /** Reports mutation state up so the shell can block its dismiss paths. */
    onBusyChange: (busy: boolean) => void;
}

interface CandidateRowProps {
    user: UserListItem;
    onPick: (user: UserListItem) => void;
}

function CandidateRow({ user, onPick }: CandidateRowProps) {
    return (
        <button
            type="button"
            onClick={() => onPick(user)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
        >
            <div className="flex-1 min-w-0">
                <UserCell name={user.fullname} email={user.email} size={32} />
            </div>
            <RoleBadge isAdmin={user.isAdmin} />
        </button>
    );
}

/**
 * "Existing user" pane of AddPracticeUserModal - links an already-existing
 * user to this practice by appending its id to that user's `practices`
 * array via PUT /users/:email (useUpdateUser), the same endpoint the global
 * Users drawer uses to change a user's practices, so this never mutates the
 * user's other practice links (see handleConfirm). Body and footer only;
 * the modal's overlay/card/header/toggle chrome lives in the parent.
 */
export function ExistingUserPane({
    practiceId,
    practiceName,
    existingEmails,
    onClose,
    onBusyChange
}: ExistingUserPaneProps) {
    const { showToast } = useToast();
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
    const [selected, setSelected] = useState<UserListItem | null>(null);

    const { users, isLoading } = useUsersList({
        page: 1,
        limit: CANDIDATE_LIMIT,
        search: debouncedSearch.trim() || undefined
    });
    const candidates = users.filter((user) => !existingEmails.has(user.email));

    const updateUser = useUpdateUser();

    useEffect(() => {
        onBusyChange(updateUser.isPending);
        // Clear on unmount: the shell gates every dismiss path on this flag, so a
        // pane torn down mid-request would otherwise strand the modal with no way out.
        return () => onBusyChange(false);
    }, [updateUser.isPending, onBusyChange]);

    const handleConfirm = async () => {
        if (!selected) return;
        try {
            // The picked row carries its OWN current practices - append this
            // practice's id to that list rather than sending only this id,
            // or the PUT would clobber every other practice the user is
            // already linked to.
            const practices = Array.from(
                new Set([...selected.practices.map((practice) => practice.id), practiceId])
            );
            await updateUser.mutateAsync({ email: selected.email, patch: { practices } });
            showToast(`${selected.fullname} added to ${practiceName}`, 'success');
            onClose();
        } catch (error) {
            showToast(apiErrorMessage(error, 'Failed to add user to practice'), 'error');
        }
    };

    return (
        <>
            <div className="px-6 pb-5 flex flex-col gap-3">
                <p className="text-[13px] text-gray-600 leading-relaxed">
                    Link a user who already has an account to this practice.
                </p>
                {selected ? (
                    <div className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2.5">
                        <div className="flex-1 min-w-0">
                            <UserCell name={selected.fullname} email={selected.email} size={32} />
                        </div>
                        <button
                            type="button"
                            onClick={() => setSelected(null)}
                            className="text-[12px] font-semibold text-gray-500 hover:text-gray-900 shrink-0"
                        >
                            Change
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 h-10">
                            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 shrink-0" />
                            <input
                                type="text"
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search users by name or email"
                                className="flex-1 min-w-0 text-sm text-gray-700 placeholder-gray-400 outline-none bg-transparent"
                            />
                        </div>
                        <div className="max-h-[260px] overflow-y-auto flex flex-col gap-0.5">
                            {isLoading && (
                                <p className="px-3 py-3 text-[13px] text-gray-400">Searching…</p>
                            )}
                            {!isLoading && candidates.length === 0 && (
                                <p className="px-3 py-3 text-[13px] text-gray-400">
                                    No matching users found.
                                </p>
                            )}
                            {!isLoading &&
                                candidates.map((user) => (
                                    <CandidateRow key={user.id} user={user} onPick={setSelected} />
                                ))}
                        </div>
                    </>
                )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2.5">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={updateUser.isPending}
                    className="text-sm font-semibold text-gray-500 hover:text-gray-900 px-3 h-10 disabled:opacity-50"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    disabled={!selected || updateUser.isPending}
                    onClick={handleConfirm}
                    className="bg-black text-white font-semibold px-5 h-10 rounded-xl text-sm hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {updateUser.isPending ? 'Adding…' : `Add to ${practiceName}`}
                </button>
            </div>
        </>
    );
}
